import Button from '@ingka/button';
import Loading, { LoadingBall } from '@ingka/loading';
import information from '@ingka/ssr-icon/paths/information';
import Tooltip from '@ingka/tooltip';
import type { LotteryEditPhase } from '@lotta/feature-lottery-rules';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { DateTime } from 'luxon';
import { memo, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../LotteryForm.module.scss';
import { ControlledDateInput } from '../../components/controlled-date-input/ControlledDateInput';
import { ControlledDrawTimeDropdown } from '../../components/controlled-draw-time-dropdown/ControlledDrawTimeDropdown';
import { ControlledQuantityStepper } from '../../components/controlled-quantity-stepper/ControlledQuantityStepper';
import { LotteryFormContext, allowFieldInput } from '../../lottery-form-context';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../types';
import { SingleDrawDaysPreview } from './draw-days-preview/SingleDrawDaysPreview';
import { fromPayload } from './from-payload';
import { toPayload } from './to-payload';

export const MODULE_NAME = 'single-occurrence';

export type SingleOccurrenceFormType = Static<typeof SingleOccurrenceFormSchema>;
export const SingleOccurrenceFormSchema = Type.Object(
  {
    startDate: Type.String({
      format: 'date',
      formatMinimum: DateTime.now().toISODate(),
      errorMessage: {
        format: 'form-error-message-date-invalid-format',
        formatMinimum: 'form-start-month-error-message-minimum',
        formatMaximum: 'form-start-month-error-message-maximum',
      },
    }),
    endDate: Type.String({
      format: 'date',
      formatMinimum: { $data: '1/startDate' },
      errorMessage: {
        format: 'form-error-message-date-invalid-format',
        formatMinimum: 'form-end-month-error-message-minimum',
        formatMaximum: 'form-end-month-error-message-maximum',
      },
    }),
    drawDay: Type.Integer({
      minimum: 1,
      maximum: 15,
      errorMessage: {
        type: 'form-error-message-minimum-day-value',
        minimum: 'form-error-message-minimum-day-value',
        maximum: 'form-error-message-maximum-numeric-value',
      },
    }),
    drawTime: Type.RegEx(/^(0[0-9]|1[0-9]|2[0-3]):00$/, {
      errorMessage: {
        pattern: 'form-error-message-required',
      },
    }),
  },
  { additionalProperties: false, $id: 'SingleOccurrenceFormSchema' }
);

const editEnabledConfig: Record<keyof SingleOccurrenceFormType, LotteryEditPhase[]> = {
  startDate: ['OPEN', 'COPY'],
  endDate: ['OPEN', 'LIMITED', 'COPY'],
  drawDay: ['OPEN', 'LIMITED', 'COPY'],
  drawTime: ['OPEN', 'LIMITED', 'COPY'],
};

const getSchema = (initialValues: SingleOccurrenceFormType | undefined) => {
  if (!initialValues) {
    return SingleOccurrenceFormSchema;
  }
  // For edit lottery, modify validation for startDate to allow leaving it unchanged
  return Type.Object(
    {
      ...SingleOccurrenceFormSchema.properties,
      startDate: Type.String({
        ...SingleOccurrenceFormSchema.properties.startDate,
        formatMinimum: DateTime.min(
          DateTime.fromISO(initialValues.startDate),
          DateTime.now()
        ).toISODate(),
      }),
    },
    { additionalProperties: false, $id: 'UpdateSingleOccurrenceFormSchema' }
  );
};

export const SingleOccurrenceFormTransformer: ModuleTransformer<SingleOccurrenceFormType> = {
  fromPayload,
  toPayload,
};

const defaultValues: SingleOccurrenceFormType = {
  startDate: '',
  endDate: '',
  drawDay: 1,
  drawTime: '',
};

export type SingleOccurrenceFormProps = FormModuleProps<SingleOccurrenceFormType>;

export const SingleOccurrenceForm: React.FC<SingleOccurrenceFormProps> = memo(
  ({ initialValues, retailUnit, values, onChange, onValidation }) => {
    const lotteryFormContext = useContext(LotteryFormContext);
    const { data: retailUnitData, isLoading: retailUnitIsLoading } = useGetRetailUnitQuery({
      retailUnit,
    });
    const { t } = useTranslation();
    const { locale } = useLocale();

    const schema = useMemo(() => getSchema(initialValues), [initialValues]);

    const {
      control,
      formState: { errors },
    } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema,
      onChange,
      onValidation,
    });

    if (retailUnitIsLoading) {
      return (
        <Loading text={`${t('general-fetching-template')}`}>
          <LoadingBall />
        </Loading>
      );
    }

    const timeZone = retailUnitData?.retailUnit.timeZone ?? '';

    return (
      <form>
        <div className={styles['form-elements']}>
          <ControlledDateInput
            name="startDate"
            control={control}
            disabled={!allowFieldInput(editEnabledConfig.startDate, lotteryFormContext)}
            locale={locale}
            labelKey="general-start-date"
          />
        </div>
        <div className={styles['form-elements']}>
          <ControlledDateInput
            name="endDate"
            control={control}
            disabled={!allowFieldInput(editEnabledConfig.endDate, lotteryFormContext)}
            locale={locale}
            labelKey="general-end-date"
          />
        </div>
        <div className={styles['form-elements']}>
          <ControlledQuantityStepper
            name="drawDay"
            control={control}
            disabled={!allowFieldInput(editEnabledConfig.drawDay, lotteryFormContext)}
            label={`${t('lottery-form-draw-day-stepper-label')}`}
            minVal={SingleOccurrenceFormSchema.properties.drawDay.minimum ?? 1}
            maxVal={SingleOccurrenceFormSchema.properties.drawDay.maximum ?? 30}
          />
        </div>
        <div className={styles['tool-tip-container']}>
          <div className={styles['form-elements']}>
            <ControlledDrawTimeDropdown
              name="drawTime"
              control={control}
              disabled={!allowFieldInput(editEnabledConfig.drawTime, lotteryFormContext)}
              errorMessage={errors.drawTime?.message as string}
              timeZone={timeZone}
            />
          </div>
          <div className={styles['tool-tip']}>
            <Tooltip
              tooltipText={t('lottery-form-draw-time-dropdown-tooltip')}
              ssrIcon={information}
              position="trailing"
            >
              <Button iconOnly ssrIcon={information} type="tertiary" small />
            </Tooltip>
          </div>
        </div>
        <SingleDrawDaysPreview control={control} timezone={timeZone} />
      </form>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: SingleOccurrenceFormTransformer,
  form: SingleOccurrenceForm,
} as const;
